/**
 * Supervisory Visits Tab — RN supervisory visit cycle for one client:
 * next due / overdue status, book or start the visit, history of completed visits.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import StartAssessmentDialog from "@/components/assessments/StartAssessmentDialog";
import AssessmentFiller from "@/components/assessments/AssessmentFiller";
import AssessmentList from "@/components/assessments/AssessmentList";
import ScheduleVisitDialog from "@/components/assessments/ScheduleVisitDialog";
import { DueBadge, ScheduledInfo } from "@/components/assessments/DuePanel";
import { fmtDate } from "@/components/assessments/assessmentMeta";
import { CalendarPlus, ChevronDown, ChevronRight, Eye, Play, Plus, Settings2, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SupervisoryVisitsTab({ clientId, clientName, supervisoryVisits = [], onDeleteVisit, onCompleted }) {
  const { user } = useAuth();
  const role = (user?.role || "").toLowerCase();
  const canEdit = ["rn", "admin"].includes(role);
  const [items, setItems] = useState(null);
  const [due, setDue] = useState(null);
  const [settings, setSettings] = useState({});
  const [starting, setStarting] = useState(false);
  const [scheduling, setScheduling] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [showLegacy, setShowLegacy] = useState(false);
  const [recurring, setRecurring] = useState(null); // Plan of Care & other forms re-signed at each visit

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/assessments`, { params: { client_id: clientId, assessment_type: "supervisory" } });
      setItems(r.data.assessments);
      const rr = await axios.get(`${API}/assessments`, { params: { client_id: clientId, recurring: true } });
      setRecurring(rr.data.assessments.filter((a) => a.assessment_type !== "supervisory"));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load supervisory visits");
      setItems([]);
    }
    axios.get(`${API}/assessments/due`, { params: { client_id: clientId } })
      .then((r) => { setDue((r.data.items || []).find((i) => i.kind === "supervisory") || null); setSettings(r.data.settings || {}); })
      .catch(() => setDue(null));
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  const closeFiller = useCallback(() => setOpenId(null), []);

  const tone = !due ? "border-slate-200 bg-slate-50 text-slate-700" : due.status === "overdue" ? "border-red-200 bg-red-50 text-red-900" : due.status === "due_soon" ? "border-amber-200 bg-amber-50 text-amber-900" : "border-emerald-200 bg-emerald-50 text-emerald-900";

  return (
    <div data-testid="client-supervisory-tab" className="space-y-6">
      <div className="form-section">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2"><Eye className="w-5 h-5 text-sky-600" /> Supervisory Visits</h3>
            <p className="text-sm text-slate-500">
              Every {settings.supervisory_interval_days || 60} days · reminder {settings.due_soon_days ?? 7} days before due.
              {role === "admin" && <> <Link to="/settings?tab=visit-reminders" className="text-blue-600 hover:underline inline-flex items-center gap-1 ml-1"><Settings2 className="w-3 h-3" /> Change</Link></>}
            </p>
          </div>
          {canEdit && (
            <Button size="sm" onClick={() => setStarting(true)} className="gap-2 bg-sky-600 hover:bg-sky-700" data-testid="new-supervisory-visit-btn">
              <Plus className="w-4 h-4" /> Start supervisory visit
            </Button>
          )}
        </div>

        <div className={`rounded-lg border px-4 py-3 flex flex-wrap items-center gap-3 mb-4 ${tone}`} data-testid="supervisory-due-banner">
          <div className="flex-1 min-w-[200px]">
            {due ? (
              <>
                <p className="font-medium">Next supervisory visit due {fmtDate(due.due_date)}</p>
                <p className="text-xs opacity-80">Last visit {fmtDate(due.last_date)}{due.last_source === "admission" ? " (admission)" : due.last_source === "client_start" ? " (client start)" : ""} · every {due.interval_days} days</p>
              </>
            ) : (
              <p className="font-medium">No supervisory cycle yet — starts once the client is active.</p>
            )}
          </div>
          {due && <DueBadge item={due} />}
          {due && <ScheduledInfo item={due} />}
          {due && canEdit && !due.scheduled_appointment && (
            <Button size="sm" variant="outline" className="h-8 gap-1 bg-white" onClick={() => setScheduling(due)} data-testid="supervisory-schedule-btn">
              <CalendarPlus className="w-3.5 h-3.5" /> Schedule visit
            </Button>
          )}
          {due && canEdit && due.status !== "ok" && (
            <Button size="sm" className="h-8 gap-1 bg-rose-600 hover:bg-rose-700" onClick={() => setStarting(true)} data-testid="supervisory-start-now-btn">
              <Play className="w-3.5 h-3.5" /> Start now
            </Button>
          )}
        </div>

        <AssessmentList
          items={items}
          canEdit={canEdit}
          onOpen={(a) => setOpenId(a.id)}
          onChanged={load}
          emptyText={canEdit ? "No supervisory visits recorded yet — click “Start supervisory visit” when the RN is in the home." : "No supervisory visits recorded yet."}
        />
      </div>

      {recurring && recurring.length > 0 && (
        <div data-testid="recurring-forms-section">
          <h3 className="text-sm font-semibold text-slate-800 mb-1">Forms updated at each supervisory visit</h3>
          <p className="text-xs text-slate-500 mb-3">Plan of Care and similar forms carry their content forward; the nurse re-signs and dates them at every visit. Use “Versions” to see the chain.</p>
          <AssessmentList items={recurring} canEdit={canEdit} onOpen={(a) => setOpenId(a.id)} onChanged={load} />
        </div>
      )}

      {supervisoryVisits.length > 0 && (
        <div className="form-section">
          <button type="button" onClick={() => setShowLegacy((v) => !v)} className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900" data-testid="legacy-visits-toggle">
            {showLegacy ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            Earlier supervisory visit log ({supervisoryVisits.length})
          </button>
          {showLegacy && (
            <div className="space-y-2 mt-3">
              {supervisoryVisits.map((visit) => (
                <div key={visit.id} className="p-3 border border-slate-200 rounded-lg bg-white flex items-start justify-between gap-3" data-testid={`supervisory-visit-${visit.id}`}>
                  <div className="text-sm">
                    <p className="font-medium text-slate-800">{visit.visit_date} · {visit.supervisor_name}</p>
                    {visit.notes && <p className="text-slate-500 italic mt-0.5">{visit.notes}</p>}
                  </div>
                  {onDeleteVisit && (
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => onDeleteVisit(visit.id)} data-testid={`delete-supervisory-${visit.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <StartAssessmentDialog open={starting} onClose={() => setStarting(false)} clientId={clientId} clientName={clientName} defaultType="supervisory" lockType onStarted={(first) => { load(); if (first) setOpenId(first.id); }} />
      <ScheduleVisitDialog item={scheduling} onClose={() => setScheduling(null)} onScheduled={load} />
      {openId && <AssessmentFiller assessmentId={openId} onClose={closeFiller} onCompleted={() => { load(); onCompleted?.(); }} />}
    </div>
  );
}
